/**
 * GamePerformanceStripCard - Team performance card wrapping the strip chart.
 *
 * Shows: team header with rank + record + PPG sparkline, W/L strip with
 * strength badges, and the multi-dimensional performance chart.
 *
 * Sport-agnostic: accepts generic team/games/stats data.
 *
 * @param {Object} team - {name, abbreviation, logo, ap_rank, conference}
 * @param {Object} stats - {season: {wins, losses, conf_wins, conf_losses, ppg, papg}}
 * @param {Object} momentum - {status, label, current_streak, streak_type}
 * @param {Array} recentGames - Recent games array for the strip chart
 * @param {number[]} ppgTrend - PPG values for inline sparkline
 */
import Card from '../../common/Card';
import InlineSparkline from './InlineSparkline';
import GamePerformanceStrip from './GamePerformanceStrip';

const QUALITY_DOT = {
  elite: 'text-yellow-400',
  strong: 'text-blue-400',
  average: 'text-gray-500',
  weak: 'text-gray-700',
};

const GamePerformanceStripCard = ({
  team = {},
  stats = {},
  momentum = {},
  recentGames = [],
  ppgTrend = [],
}) => {
  const season = stats.season || {};
  const record = `${season.wins || 0}-${season.losses || 0}`;
  const confRecord = season.conf_wins !== undefined
    ? `${season.conf_wins}-${season.conf_losses}`
    : null;
  const ppg = season.ppg || season.points_for_avg || season.goals_for_avg || 0;

  // Calculate season PPG for the reference line
  const seasonAvg = ppg;

  return (
    <Card className="p-3">
      {/* Team Header */}
      <div className="flex items-center gap-2 mb-2">
        {team.logo && (
          <img
            src={team.logo}
            alt={team.name}
            className="w-7 h-7 object-contain flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            {team.ap_rank && (
              <span className="text-[10px] font-bold text-yellow-400">
                #{team.ap_rank}
              </span>
            )}
            <span className="text-sm font-bold text-text-primary truncate">
              {team.name || team.abbreviation}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-text-secondary">
            <span className="font-mono">{record}</span>
            {confRecord && (
              <span className="opacity-60">({confRecord} conf)</span>
            )}
            {team.conference && (
              <span className="opacity-40">{team.conference}</span>
            )}
          </div>
        </div>

        {/* PPG with sparkline */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="font-mono text-sm font-bold text-accent-blue">
            {ppg}
          </span>
          <span className="text-[9px] text-text-secondary">PPG</span>
          {ppgTrend.length >= 2 && (
            <InlineSparkline
              data={ppgTrend}
              width={48}
              height={14}
              color="#00D9FF"
              referenceLine={seasonAvg}
              dotColor="#00D9FF"
            />
          )}
        </div>
      </div>

      {/* SOS-adjusted W-L strip: colored squares with quality dots */}
      {recentGames.length > 0 && (
        <div className="flex items-center gap-0.5 mb-2 px-0.5">
          {recentGames.filter(g => g.team_score > 0 || g.opp_score > 0).map((g, i) => (
            <div key={i} className="flex flex-col items-center">
              <div
                className={`w-5 h-5 rounded-sm flex items-center justify-center text-[9px] font-bold ${
                  g.result === 'W'
                    ? 'bg-success/20 text-success border border-success/30'
                    : 'bg-danger/20 text-danger border border-danger/30'
                }`}
              >
                {g.result}
              </div>
              {/* Quality indicator dot */}
              <span className={`text-[6px] mt-0.5 ${QUALITY_DOT[g.opponent_quality] || QUALITY_DOT.average}`}>
                {g.opponent_quality === 'elite' ? '\u25CF' :
                 g.opponent_quality === 'strong' ? '\u25CF' :
                 g.opponent_quality === 'average' ? '\u25CB' :
                 '\u25CB'}
              </span>
            </div>
          ))}

          {/* Streak label */}
          {momentum.status && momentum.status !== 'neutral' && (
            <span className={`ml-2 text-[10px] font-semibold ${
              momentum.status === 'hot' || momentum.status === 'trending_up'
                ? 'text-success'
                : momentum.status === 'cold' || momentum.status === 'trending_down'
                  ? 'text-danger'
                  : 'text-text-secondary'
            }`}>
              {momentum.label}
            </span>
          )}
        </div>
      )}

      {/* Performance Strip Chart */}
      {recentGames.length > 0 && (
        <GamePerformanceStrip
          games={recentGames}
          seasonAvg={seasonAvg}
        />
      )}
    </Card>
  );
};

export default GamePerformanceStripCard;
